import { z } from 'zod';
import { brandColorPattern } from './organization.js';
/**
 * Referrer partners („Tippgeber-Partner", owner decision 14.10.2026, D-248): external organizations without a login
 * that submit self-disclosures for prospects through their standing public link `/t/<token>`. Shared by the API
 * (settings `/api/settings/organization/referrer-partners`, docs/dev/api-referrer-partners.md) and the SPA (settings
 * page „Tippgeber", the partner filters of the contact list and the deal board).
 */
/** Days a partner's intake link stays valid; rotating it starts a new period. */
export const referrerIntakeLinkDays = 730;
export const REFERRER_PARTNER_NAME_MESSAGE = 'Bitte geben Sie einen Namen mit 2 bis 120 Zeichen an.';
export const REFERRER_PARTNER_SLUG_MESSAGE = 'Bitte nur Kleinbuchstaben, Ziffern und Bindestriche verwenden (2 bis 60 Zeichen).';
export const REFERRER_PARTNER_EMAIL_MESSAGE = 'Bitte geben Sie eine gültige E-Mail-Adresse an.';
export const REFERRER_PARTNER_COLOR_MESSAGE = 'Bitte eine Farbe im Format #RRGGBB angeben.';
export const referrerPartnerSlugPattern = /^[a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])$/;
const isoDate = z.string();
export const referrerPartnerSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    contactName: z.string().nullable(),
    contactEmail: z.string().nullable(),
    brandColor: z.string().nullable(),
    hasLogo: z.boolean(),
    /** Authenticated preview `GET /api/settings/organization/referrer-partners/:id/logo`; null without a logo. */
    logoUrl: z.string().nullable(),
    defaultCloserId: z.string().uuid().nullable(),
    defaultCloserName: z.string().nullable(),
    active: z.boolean(),
    note: z.string().nullable(),
    /** The public link `<organization base URL>/t/<token>` („Link kopieren"). */
    intakeUrl: z.string(),
    intakeExpiresAt: isoDate,
    submissionCount: z.number().int(),
    /** Distinct submitters („Einreicher") who used the link (lower-cased e-mail of step 5). */
    submitterCount: z.number().int(),
    lastSubmissionAt: isoDate.nullable(),
    createdAt: isoDate,
});
export const referrerPartnerListSchema = z.object({ items: z.array(referrerPartnerSchema) });
const nameSchema = z
    .string({ error: REFERRER_PARTNER_NAME_MESSAGE })
    .trim()
    .min(2, { error: REFERRER_PARTNER_NAME_MESSAGE })
    .max(120, { error: REFERRER_PARTNER_NAME_MESSAGE });
const slugSchema = z
    .string({ error: REFERRER_PARTNER_SLUG_MESSAGE })
    .trim()
    .toLowerCase()
    .regex(referrerPartnerSlugPattern, { error: REFERRER_PARTNER_SLUG_MESSAGE });
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((value) => (value === '' ? null : value));
const emailFormat = z.email();
const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(200, { error: REFERRER_PARTNER_EMAIL_MESSAGE })
    .refine((value) => value === '' || emailFormat.safeParse(value).success, {
    error: REFERRER_PARTNER_EMAIL_MESSAGE,
})
    .nullable()
    .optional()
    .transform((value) => (value === '' ? null : value));
const colorSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(brandColorPattern, { error: REFERRER_PARTNER_COLOR_MESSAGE })
    .nullable()
    .optional();
export const createReferrerPartnerBodySchema = z.object({
    name: nameSchema,
    /** Optional; derived from the name (ä → ae, …) and made unique within the organization. */
    slug: slugSchema.optional(),
    contactName: optionalText(120),
    contactEmail: emailSchema,
    brandColor: colorSchema,
    defaultCloserId: z.string().uuid().nullable().optional(),
    note: optionalText(2000),
});
export const updateReferrerPartnerBodySchema = z.object({
    name: nameSchema.optional(),
    slug: slugSchema.optional(),
    contactName: optionalText(120),
    contactEmail: emailSchema,
    brandColor: colorSchema,
    defaultCloserId: z.string().uuid().nullable().optional(),
    note: optionalText(2000),
    active: z.boolean().optional(),
});
/** The partner as a filter option (contact list, deal board). */
export const referrerPartnerOptionSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    active: z.boolean(),
});
export const referrerPartnerOptionListSchema = z.object({ items: z.array(referrerPartnerOptionSchema) });
/**
 * A submitter („Einreicher", owner decision 02.11.2026): a person who filled in the partner's link — the partner's own
 * sales partners use the same link. Identified by the lower-cased e-mail within the partner; name, e-mail and phone of
 * the most recent submission with that e-mail.
 */
export const referrerSubmitterSchema = z.object({
    key: z.string(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    submissions: z.number().int(),
    lastSubmissionAt: isoDate,
});
/** `GET …/referrer-partners/:id/submitters` — by submissions (desc), then the most recent, then name. */
export const referrerSubmitterListSchema = z.object({ items: z.array(referrerSubmitterSchema) });
/** Settings „Einreichungen" cell: „4 · von 2 Personen" once at least two people submitted through the link. */
export function submissionCountLabel(submissions, submitters) {
    return submitters >= 2 ? `${submissions} · von ${submitters} Personen` : String(submissions);
}
/** „Tippgeber · <Partner>" — the source line of contacts and deals that came in through a partner. */
export function referrerSourceLabel(partnerName) {
    return `Tippgeber · ${partnerName}`;
}
/** The `source_channel` value of a contact created by a referrer intake. */
export const REFERRER_SOURCE_CHANNEL = 'Tippgeber';
//# sourceMappingURL=referrer-partners.js.map